import { useEffect, useRef, useState } from "react";
import { HERO_STATS } from "@/data/content";

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

function Counter({ value, suffix, active }) {
  const [n, setN] = useState(0);
  const target = parseInt(value, 10);
  useEffect(() => {
    if (!active) return;
    const duration = 1400;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target]);
  return (
    <span>
      {n}
      {suffix}
    </span>
  );
}

export default function StatsBanner({ dark = false }) {
  const ref = useRef(null);
  const inView = useInView(ref);

  return (
    <section
      ref={ref}
      data-testid="stats-banner"
      className={`${
        dark ? "bg-[#0B2341] text-white" : "bg-[#F8F9FA] text-[#0B2341]"
      } border-y ${dark ? "border-[#1E3A5F]" : "border-[#E2E8F0]"}`}
    >
      <div className="container-corp py-14 md:py-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-8">
          {HERO_STATS.map((s, i) => (
            <div
              key={s.label}
              data-testid={`stat-${i}`}
              className={`flex flex-col ${
                i !== 0 ? "lg:border-l lg:pl-10" : ""
              } ${dark ? "lg:border-white/15" : "lg:border-[#E2E8F0]"}`}
            >
              <div className="font-heading text-5xl md:text-6xl font-semibold tracking-tight">
                <Counter value={s.value} suffix={s.suffix} active={inView} />
              </div>
              <div
                className={`mt-3 text-xs uppercase tracking-[0.22em] font-medium ${
                  dark ? "text-[#16A34A]" : "text-[#4A5568]"
                }`}
              >
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
